/**
 * Abas da ficha: fixas no topo ao rolar (sombra quando presas), indicador
 * que desliza entre elas e conteúdo em crossfade com ±12 px na direção da
 * troca. ←/→ trocam de aba.
 */
import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import { CURVA_ENTRADA, CURVA_SAIDA, MOLA } from "../comum/movimento";

function usePreso(ref) {
  const [preso, setPreso] = useState(false);
  useEffect(() => {
    const alvo = ref.current;
    if (!alvo || typeof IntersectionObserver === "undefined") return undefined;
    // Sentinela logo acima das abas: quando ela some atrás do cabeçalho, as abas estão presas.
    const sentinela = document.createElement("div");
    sentinela.style.height = "1px";
    alvo.parentNode.insertBefore(sentinela, alvo);
    const topo = parseInt(getComputedStyle(alvo).top, 10) || 0;
    const observador = new IntersectionObserver(([e]) => setPreso(!e.isIntersecting), { rootMargin: `-${topo + 1}px 0px 0px 0px` });
    observador.observe(sentinela);
    return () => {
      observador.disconnect();
      sentinela.remove();
    };
  }, [ref]);
  return preso;
}

export function BarraDeAbas({ abas, ativa, aoTrocar }) {
  const ref = useRef(null);
  const preso = usePreso(ref);
  function tecla(evento) {
    if (evento.key !== "ArrowRight" && evento.key !== "ArrowLeft") return;
    const visiveis = abas.filter((a) => !a.oculta);
    const i = visiveis.findIndex((a) => a.id === ativa);
    const proxima = visiveis[(i + (evento.key === "ArrowRight" ? 1 : visiveis.length - 1)) % visiveis.length];
    aoTrocar(proxima.id);
    evento.currentTarget.querySelector(`[data-aba="${proxima.id}"]`)?.focus();
  }
  return (
    <div className="cl-mtabs" role="tablist" aria-label="Seções do cliente" ref={ref} data-preso={preso} onKeyDown={tecla}>
      {abas.filter((a) => !a.oculta).map((aba) => {
        const selecionada = aba.id === ativa;
        return (
          <button
            key={aba.id}
            type="button"
            role="tab"
            id={`aba-${aba.id}`}
            data-aba={aba.id}
            aria-selected={selecionada}
            aria-controls={`painel-${aba.id}`}
            tabIndex={selecionada ? 0 : -1}
            className="cl-mt"
            data-so-estreito={aba.soEstreito ? "true" : undefined}
            onClick={() => aoTrocar(aba.id)}
          >
            {aba.rotulo}
            <b className={aba.alerta ? "alerta" : undefined}>{aba.total}</b>
            {selecionada && <motion.span layoutId="cl-aba-ficha" className="cl-mt-ind" transition={MOLA} />}
          </button>
        );
      })}
    </div>
  );
}

const VARIANTES = {
  entra: (direcao) => ({ opacity: 0, x: 12 * direcao }),
  fica: { opacity: 1, x: 0, transition: { duration: 0.26, ease: CURVA_ENTRADA } },
  sai: (direcao) => ({ opacity: 0, x: -12 * direcao, transition: { duration: 0.16, ease: CURVA_SAIDA } }),
};

/** Conteúdo da aba ativa com crossfade direcional. */
export function PainelDaAba({ id, direcao, children }) {
  return (
    <AnimatePresence mode="wait" custom={direcao} initial={false}>
      <motion.div
        key={id}
        className="cl-painel"
        role="tabpanel"
        id={`painel-${id}`}
        aria-labelledby={`aba-${id}`}
        custom={direcao}
        variants={VARIANTES}
        initial="entra"
        animate="fica"
        exit="sai"
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}
